import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHero from '../components/PageHero';
import CtaBand from '../components/CtaBand';
import { projects } from '../data/site';

const { FiX, FiChevronLeft, FiChevronRight, FiMaximize2 } = FiIcons;
const filters = ['All', 'Driveways', 'Patios', 'Pool Decks', 'Walkways'];

function Lightbox({ items, index, onClose, onMove }) {
  const p = items[index];
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const key = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onMove(1);
      if (e.key === 'ArrowLeft') onMove(-1);
    };
    document.addEventListener('keydown', key);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', key);
    };
  }, [onClose, onMove]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4 md:p-16"
      role="dialog"
      aria-modal="true"
      aria-label={p.title}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button type="button" aria-label="Close" onClick={onClose} className="absolute right-5 top-5 grid h-12 w-12 place-items-center border border-white/30 text-2xl text-white hover:bg-white/10">
        <SafeIcon icon={FiX} />
      </button>
      <button type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); onMove(-1); }} className="absolute bottom-5 left-5 grid h-12 w-12 place-items-center border border-white/30 text-2xl text-white hover:bg-white/10 md:bottom-auto md:left-6">
        <SafeIcon icon={FiChevronLeft} />
      </button>
      <button type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); onMove(1); }} className="absolute bottom-5 right-5 grid h-12 w-12 place-items-center border border-white/30 text-2xl text-white hover:bg-white/10 md:bottom-auto md:right-6">
        <SafeIcon icon={FiChevronRight} />
      </button>
      <div className="w-full max-w-5xl text-white" onClick={(e) => e.stopPropagation()}>
        <img src={p.image} alt={p.title} className="max-h-[70vh] w-full object-contain" />
        <div className="mt-5 flex justify-between gap-4">
          <div>
            <p className="eyebrow !text-clay">{p.category} / {p.material}</p>
            <h3 className="h-display mt-2 text-4xl">{p.title}</h3>
            <p className="mt-1 text-sm text-white/65">{p.text}</p>
          </div>
          <p className="text-xs text-white/50">{index + 1} / {items.length}</p>
        </div>
      </div>
    </motion.div>
  );
}

function Gallery() {
  const [filter, setFilter] = useState('All');
  const [open, setOpen] = useState(null);
  const items = filter === 'All' ? projects : projects.filter((p) => p.category === filter);
  const move = (d) => setOpen((i) => (i + d + items.length) % items.length);

  return (
    <>
      <PageHero
        eyebrow="Gallery"
        title="Get"
        accent="inspired."
        text="Browse design ideas by space. Imagery shown is inspiration for what is possible, not completed US Paver Co. projects."
        image={projects[2].image}
      />
      <section className="py-20 md:py-28">
        <div className="wrap">
          <div className="mb-10 flex flex-wrap gap-2" role="group" aria-label="Filter gallery">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={filter === f}
                onClick={() => { setFilter(f); setOpen(null); }}
                className={`px-5 py-3 text-xs font-bold uppercase tracking-widest transition ${
                  filter === f ? 'bg-ink text-white' : 'border border-ink/20 hover:bg-ink hover:text-white'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <motion.div layout className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {items.map((p, i) => (
                <motion.button
                  layout
                  key={p.title}
                  type="button"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  onClick={() => setOpen(i)}
                  className="group relative aspect-[4/5] overflow-hidden text-left text-white"
                  aria-label={`View ${p.title}`}
                >
                  <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  <span className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                  <span className="absolute right-4 top-4 grid h-10 w-10 place-items-center bg-bone/90 text-ink opacity-0 transition group-hover:opacity-100">
                    <SafeIcon icon={FiMaximize2} />
                  </span>
                  <span className="absolute inset-x-5 bottom-5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-clay">{p.category} · {p.material}</span>
                    <span className="h-display mt-1 block text-3xl">{p.title}</span>
                  </span>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      <AnimatePresence>
        {open !== null && <Lightbox items={items} index={open} onClose={() => setOpen(null)} onMove={move} />}
      </AnimatePresence>
      <CtaBand title="Found a look you love? Let's build it." />
    </>
  );
}

export default Gallery;
